/**
 * api.ts — PIEA AGI API client
 * Proxy via Vite dev server: /api/* → http://127.0.0.1:8790/*
 * Production: set VITE_API_BASE to the deployed worker URL
 * Classification: trade-secret/private
 */

declare const __API_BASE__: string;

const BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE
    ?? (typeof __API_BASE__ !== "undefined" ? __API_BASE__ : "https://piea-agi-worker-production.neil-98a.workers.dev");

// ── Auth state (module-level — simple for Phase 8) ────────────────────────────
let _apiKey = localStorage.getItem("piea_api_key") ?? "";

export function setApiKey(key: string): void {
    _apiKey = key;
    localStorage.setItem("piea_api_key", key);
}

export function getApiKey(): string {
    return _apiKey;
}

// ── Fetch wrapper ─────────────────────────────────────────────────────────────

async function apiFetch<T>(
    path: string,
    options: RequestInit = {}
): Promise<T> {
    const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(options.headers as Record<string, string> ?? {}),
    };

    if (_apiKey) {
        headers["Authorization"] = `Bearer ${_apiKey}`;
    }

    // 28-second hard timeout — keeps us inside CF Worker's 30s limit
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 28_000);

    try {
        const response = await fetch(`${BASE}${path}`, {
            ...options,
            headers,
            signal: controller.signal,
        });

        if (!response.ok) {
            const body = await response.text().catch(() => "");
            throw new ApiError(response.status, body);
        }

        return response.json() as Promise<T>;
    } finally {
        clearTimeout(timer);
    }
}

export class ApiError extends Error {
    constructor(
        public readonly status: number,
        public readonly body: string
    ) {
        super(`API ${status}: ${body}`);
    }
}

// ── Health ─────────────────────────────────────────────────────────────────────

export interface HealthResponse {
    status: string;
    deployment: string;
    genome_kernel: string;
    lockfile_version: string;
    schema_versions: Record<string, string>;
    protocol: string;
    identity_proof: IdentityProof | null;
    timestamp: string;
}

export interface IdentityProof {
    substrate_identity_hash: string;
    chain_depth: number;
    verified: boolean;
}

export function getHealth(): Promise<HealthResponse> {
    return apiFetch<HealthResponse>("/health");
}

// ── Substrate session start (P104) ────────────────────────────────────────────

export interface SessionStartResult {
    cycle_id: string;
    cycle_identity_hash: string;
    reasoning_ledger_depth: number;
}

export function startSubstrateSession(): Promise<SessionStartResult> {
    return apiFetch<SessionStartResult>("/v1/substrate/session-start", { method: "POST", body: "{}" });
}

// ── Observatory ───────────────────────────────────────────────────────────────

export interface ObservatorySnapshot {
    substrate_identity_hash: string;
    cycle_count: number;
    kernel_version: string;
    lockfile_version: string;
    active_deliberations: number;
    recent_verdicts: RecentVerdict[];
    self_model: SelfModel | null;
    world_state: WorldState;
    pending_curiosities: number;
    top_curiosity_question?: string;
    pending_predictions: number;
    calibration_summary: CalibrationItem[];
    improvement_summary: ImprovementSummary;
    aiep_l_documents_published: number;
    active_continuous_sessions: number;
    capabilities: string[];
    snapshot_hash: string;
    created_at: string;
}

export interface RecentVerdict {
    deliberation_id: string;
    verdict_type: string;
    query_class: string;
    agreement_rate: number;
    created_at: string;
}

export interface SelfModel {
    memory_count: number;
    active_goal_count: number;
    dominant_domains: string[];
    avg_confidence: number;
    recall_rate: number;
    self_model_hash: string;
    snapshot_at: string;
}

export interface WorldState {
    entity_count: number;
    edge_count: number;
    world_state_hash: string;
}

export interface CalibrationItem {
    domain: string;
    period: string;
    brier_score: number;
    prediction_count: number;
}

export interface ImprovementSummary {
    proposed: number;
    approved: number;
    applied: number;
    governance_hash: string;
}

export function getObservatory(): Promise<ObservatorySnapshot> {
    return apiFetch<ObservatorySnapshot>("/v1/observatory");
}

// ── Deliberation ──────────────────────────────────────────────────────────────

export interface DeliberateInput {
    question: string;
    project_id?: string;
    requested_depth?: "CONVERSATIONAL" | "ANALYTICAL" | "DEEP_RESEARCH";
}

export interface DeliberateResponse {
    deliberation_id: string;
    workflow_id: string;
    status: string;
}

export interface DeliberationStatus {
    deliberation_id: string;
    status: string;
    workflow_id: string | null;
}

export function deliberate(input: DeliberateInput): Promise<DeliberateResponse> {
    return apiFetch<DeliberateResponse>("/v1/deliberate", {
        method: "POST",
        body: JSON.stringify(input),
    });
}

export interface UncertaintyRecord {
    evidence_contestation_score: number;
    branch_divergence_score: number;
    substrate_boundary_flag: boolean;
    resolution_path_indicator: string;
    uncertainty_hash: string;
}

export interface SyncDeliberationResult {
    deliberation_id: string;
    duration_ms: number;
    ai_powered?: boolean;
    chain_depth?: number;
    verdict: {
        verdict_id: string;
        deliberation_id: string;
        verdict_type: string;
        verified_answer?: string;
        verified_sources: Array<{ source_url: string; snippet?: string; provenance_class?: string }>;
        verified_confidence: number;
        theory_content?: string;
        theory_status: string;
        what_would_confirm?: string;
        instinct_content?: string;
        instinct_basis: string;
        escalation_reason?: string;
        dissent_records: number;
        dissent_level: number;
        uncertainty_record?: UncertaintyRecord;
        verdict_hash: string;
        created_at: string;
    };
}

export function deliberateSync(input: DeliberateInput): Promise<SyncDeliberationResult> {
    return apiFetch<SyncDeliberationResult>("/v1/deliberate/sync", {
        method: "POST",
        body: JSON.stringify(input),
    });
}

export function getDeliberationStatus(id: string): Promise<DeliberationStatus> {
    return apiFetch<DeliberationStatus>(`/v1/deliberate/${id}`);
}

export interface DeliberationRecord {
    deliberation_id: string;
    query_class: string;
    verdict_type: string | null;
    verdict_content: string | null;
    agreement_rate: number | null;
    evidence_count: number;
    hypothesis_count: number;
    created_at: string;
}

export function getDeliberationHistory(limit = 20): Promise<{ deliberations: DeliberationRecord[]; count: number }> {
    return apiFetch(`/v1/deliberations?limit=${limit}`);
}

export function publishAllCorpus(): Promise<{ ok: boolean; published: number }> {
    return apiFetch("/v1/corpus/publish-all", { method: "POST", body: "{}" });
}
export interface Project {
    project_id: string;
    name: string;
    description: string;
    status: string;
    created_at: string;
    last_active_at: string;
}

export interface ProjectsResponse {
    projects: Project[];
    count: number;
}

export function getProjects(): Promise<ProjectsResponse> {
    return apiFetch<ProjectsResponse>("/v1/projects");
}

export function createProject(input: { name: string; description?: string; domain?: string }): Promise<Project> {
    return apiFetch<Project>("/v1/projects", { method: "POST", body: JSON.stringify(input) });
}

// ── Goals ─────────────────────────────────────────────────────────────────────

export interface Goal {
    goal_id: string;
    title: string;
    description: string;
    goal_type: string;
    status: string;
    priority: number;
    domains: string[];
    completion_score: number;
    created_at: string;
}

export interface GoalsResponse { goals: Goal[]; count: number }

export function getGoals(): Promise<GoalsResponse> {
    return apiFetch<GoalsResponse>("/v1/goals");
}

export function createGoal(input: {
    title: string; description: string; goal_type: string; priority?: number; domains?: string[];
}): Promise<Goal> {
    return apiFetch<Goal>("/v1/goals", { method: "POST", body: JSON.stringify(input) });
}

// ── LTRME ─────────────────────────────────────────────────────────────────────

export interface SelfModelSummary {
    memory_count: number;
    active_goal_count: number;
    dominant_domains: string[];
    avg_confidence: number;
    recall_rate: number;
    self_model_hash: string;
    snapshot_at: string;
}

export interface MemorySearchResult {
    summary: string;
    confidence: number;
    memory_id: string;
    domain: string;
}

export function getSelfModel(): Promise<SelfModelSummary | { ok: false; note: string }> {
    return apiFetch("/v1/ltrme/self-model");
}

export function searchMemories(q: string): Promise<{ results: MemorySearchResult[]; count: number }> {
    return apiFetch(`/v1/ltrme/search?q=${encodeURIComponent(q)}`);
}

// ── Curiosity ─────────────────────────────────────────────────────────────────

export interface CuriosityRecord {
    curiosity_id: string;
    gap_type: string;
    source_domain: string;
    triggered_by: string;
    question_generated: string;
    priority_score: number;
    resolution_status: string;
    gap_descriptor: string;
    created_at: string;
}

export function getCuriosities(): Promise<{ gaps: CuriosityRecord[]; count: number }> {
    return apiFetch("/v1/curiosity");
}

// ── Predictions ───────────────────────────────────────────────────────────────

export interface Prediction {
    prediction_id: string;
    domain: string;
    prediction_type: string;
    claim: string;
    confidence: number;
    probability_lower: number;
    probability_upper: number;
    time_horizon: string;
    resolution_status: string;
    created_at: string;
}

export function getPredictions(): Promise<{ predictions: Prediction[]; count: number }> {
    return apiFetch("/v1/predictions");
}

export function resolvePrediction(id: string, outcome: boolean, resolution_evidence?: string): Promise<{ ok: boolean; outcome: string }> {
    return apiFetch(`/v1/predictions/${id}/resolve`, {
        method: "POST",
        body: JSON.stringify({ outcome, resolution_evidence }),
    });
}
export interface ImprovementRecord {
    record_id: string;
    proposal_type: string;
    description: string;
    calibration_delta: number;
    status: string;
    governance_checkpoint: string;
    created_at: string;
}

export function getSelfImprovement(): Promise<{ proposed: number; approved: number; applied: number; governance_hash: string }> {
    return apiFetch("/v1/self-improvement");
}

export function getSelfImprovementProposals(): Promise<{ proposals: ImprovementRecord[]; count: number }> {
    return apiFetch("/v1/self-improvement/proposals");
}

export function runSelfImprovementAnalysis(): Promise<{ proposals: ImprovementRecord[]; count: number }> {
    return apiFetch("/v1/self-improvement/analyse", { method: "POST", body: "{}" });
}

// ── AIEP-L ────────────────────────────────────────────────────────────────────

export interface AiepLDocument {
    document_id: string;
    title: string;
    doc_type: string;
    status: string;
    version_number: number;
    domain: string;
    validation_status: string;
    validation_errors: string[];
    created_at: string;
    published_at?: string;
}

export function validateAiepL(doc_type: string, content: unknown): Promise<{ valid: boolean; errors: string[] }> {
    return apiFetch("/v1/aiep-l/validate", {
        method: "POST",
        body: JSON.stringify({ doc_type, content }),
    });
}

export interface CreateDocInput {
    title: string;
    doc_type: "CLAIM" | "ASSERTION" | "REASONING_TRACE" | "SPEC" | "POLICY" | "DEFINITION";
    content: {
        language_version: string;
        summary: string;
        domain: string;
        claim_text?: string;
        claim_type?: string;
        confidence?: number;
        [key: string]: unknown;
    };
}

export function createAiepLDocument(input: CreateDocInput): Promise<AiepLDocumentSummary> {
    return apiFetch<AiepLDocumentSummary>("/v1/aiep-l/documents", {
        method: "POST",
        body: JSON.stringify(input),
    });
}

// ── Continuous mode ───────────────────────────────────────────────────────────

export interface ContinuousSession {
    session_id: string;
    status: string;
    trigger_mode: string;
    cycles_completed: number;
    max_cycles: number;
    current_focus?: string;
    last_cycle_at?: string;
    created_at: string;
}

export function startContinuousSession(params: {
    max_cycles?: number; goal_ids?: string[];
}): Promise<{ session_id: string; status: string }> {
    return apiFetch("/v1/continuous", { method: "POST", body: JSON.stringify(params) });
}

export function getContinuousSession(id: string): Promise<ContinuousSession> {
    return apiFetch(`/v1/continuous/${id}`);
}

export function pauseContinuousSession(id: string): Promise<{ ok: boolean }> {
    return apiFetch(`/v1/continuous/${id}/pause`, { method: "PATCH" });
}

export function getCalibrationSummary(): Promise<{ calibration: CalibrationItem[] }> {
    return apiFetch("/v1/predictions/calibration");
}
// ── Global Law Corpus ─────────────────────────────────────────────────────────

export interface CorpusStatus {
    law_documents: number;
    valid_documents: number;
    law_entities: number;
    corpus_size: number;
}

export interface SeedResult {
    seeded: number;
    skipped: number;
    errors: string[];
}

export function getCorpusStatus(): Promise<CorpusStatus> {
    return apiFetch<CorpusStatus>("/v1/corpus/status");
}

export function seedCorpus(force = false): Promise<SeedResult> {
    return apiFetch<SeedResult>("/v1/corpus/seed", { method: "POST", body: JSON.stringify({ force }) });
}

// ── AIEP-L documents ──────────────────────────────────────────────────────────

export interface AiepLDocumentSummary {
    document_id: string;
    title: string;
    doc_type: string;
    status: string;
    version_number: number;
    domain: string;
    jurisdiction?: string;
    validation_status: string;
    created_at: string;
    published_at?: string;
}

export interface AiepLDocumentFull extends AiepLDocumentSummary {
    content: {
        summary: string;
        claim_text?: string;
        domain: string;
        spec_refs?: string[];
        confidence?: number;
        [key: string]: unknown;
    };
    content_hash: string;
    version_hash: string;
}

export function getAiepLDocuments(params?: { doc_type?: string; status?: string }): Promise<{ documents: AiepLDocumentSummary[]; count: number }> {
    const qs = new URLSearchParams();
    if (params?.doc_type) qs.set("doc_type", params.doc_type);
    if (params?.status) qs.set("status", params.status);
    const q = qs.toString();
    return apiFetch(`/v1/aiep-l/documents${q ? `?${q}` : ""}`);
}

export function getAiepLDocument(id: string): Promise<AiepLDocumentFull> {
    return apiFetch<AiepLDocumentFull>(`/v1/aiep-l/documents/${id}`);
}

// ── LTRME memories ────────────────────────────────────────────────────────────

export interface LtrmeMemory {
    memory_id: string;
    memory_type: string;
    content_summary: string;
    domain: string;
    confidence_score: number;
    recall_count: number;
    consolidation_score: number;
    created_at: string;
}

export function searchLtrme(q: string): Promise<{ results: Array<{ summary: string; confidence: number; memory_id: string; domain: string }>; count: number }> {
    return apiFetch(`/v1/ltrme/search?q=${encodeURIComponent(q)}`);
}

// ── CWSG entity search ────────────────────────────────────────────────────────

export interface CwsgEntity {
    entity_id: string;
    entity_name: string;
    entity_type: string;
    domain: string;
    jurisdiction?: string;
    confidence_tier: string;
    summary: string | unknown;
    document_id?: string | unknown;
    created_at: string;
}

export interface CwsgEdge {
    edge_id: string;
    from_entity_id: string;
    to_entity_id: string;
    causal_type: string;
    confidence: number;
    domain: string;
}

export function searchCwsgEntities(q: string, domain?: string): Promise<{ entities: CwsgEntity[]; count: number }> {
    const qs = new URLSearchParams({ q });
    if (domain) qs.set("domain", domain);
    return apiFetch(`/v1/cwsg/search?${qs.toString()}`);
}

export function getCwsgEntity(id: string): Promise<{ entity: CwsgEntity; edges: CwsgEdge[] }> {
    return apiFetch(`/v1/cwsg/entity/${id}`);
}

// ── Chat ──────────────────────────────────────────────────────────────────────

export interface ChatResponse {
    message_id: string;
    mode: "chat" | "deliberate";
    content: string | null;
    verdict: SyncDeliberationResult["verdict"] | null;
    sources: Array<{ source_url: string; snippet?: string; provenance_class?: string }>;
    confidence: number;
    ai_powered: boolean;
    duration_ms: number;
    chain_depth?: number;
}

export function sendChat(message: string, mode: "chat" | "deliberate" = "chat", project_id?: string): Promise<ChatResponse> {
    return apiFetch<ChatResponse>("/v1/chat", {
        method: "POST",
        body: JSON.stringify({ message, mode, project_id }),
    });
}

// ── Signals ───────────────────────────────────────────────────────────────────

export interface SignalsFeed {
    curiosity_gaps: CuriosityRecord[];
    expiring_predictions: Prediction[];
    pending_proposals: ImprovementRecord[];
    active_sessions: Array<{
        session_id: string;
        status: string;
        trigger_mode: string;
        cycles_completed: number;
        max_cycles: number;
        current_focus?: string;
        last_cycle_at?: string;
        created_at: string;
    }>;
    generated_at: string;
}

export function getSignals(): Promise<SignalsFeed> {
    return apiFetch<SignalsFeed>("/v1/signals");
}

// ── Autonomous mode ────────────────────────────────────────────────────────────

export type AgiMode = "manual" | "autonomous";

export function getAgiMode(): AgiMode {
    return (localStorage.getItem("piea-agi-mode") as AgiMode) ?? "manual";
}

export function setAgiMode(mode: AgiMode): void {
    localStorage.setItem("piea-agi-mode", mode);
}

export function triggerAutonomousCycle(goal_ids?: string[]): Promise<{ session_id: string; status: string }> {
    return apiFetch("/v1/continuous", {
        method: "POST",
        body: JSON.stringify({ max_cycles: 3, trigger_mode: "GOAL_DRIVEN", goal_ids: goal_ids ?? [] }),
    });
}